import { useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import {
  buildAgentManifest,
  isRetiredFromLiveCatalog,
  parseAgentManifest,
  type CatalogModelIdRow,
  type PublishedOrgAgentSummary,
  type PublishedOrgAgentsResponse,
} from "@dosia/shared";
import { apiCall } from "../../lib/api-client.js";
import { ApiError, canManageOrg } from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { useRetiredMarksCatalog } from "../../lib/useRetiredMarksCatalog.js";
import {
  reviewsPendingPath,
  reviewsPromotePath,
  reviewsRejectPath,
  reviewsPublishedPath,
  reviewsPublishDirectPath,
  mcpReviewPendingPath,
  mcpReviewApprovePath,
  mcpReviewRejectPath,
} from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { Empty } from "../../components/Empty.js";
import { Modal } from "../../components/Modal.js";
import { PrimaryButton, SecondaryButton, TextInput } from "../../components/FormField.js";
import { FeishuPublishSection } from "./FeishuPublishSection.js";
import type { OrgDetailContext } from "./context.js";

/**
 * 审批中心 — 组织内员工提交物的统一审核入口，两分区：
 *  1. 技能与 Agent：dosia-enterprise 服务间通道透传（/reviews/*，org-admin + super/support 可管理，
 *     两步 promote：预览 confirmToken → 确认真发布）。
 *  2. MCP：既有 mcp-review 队列（admin-portal 自持，通过写 role.dosia.mcpServers）。
 *
 * 前身 McpReviewPanel（单 MCP 分区）已并入本页；旧路由 mcp-review → reviews redirect。
 */

// ---------- 企业 Agent 模型已下线标记（N-88） ----------

/**
 * Agent 指定的模型已从网关下线时的琥珀标记（模型名划线 + 徽标）。判据与企业 Agent 提交拒收、员工端入库
 * 归一（B-66）同一个 shared `isRetiredFromLiveCatalog`：跟随主脑（`inherit`）与 Claude 系不算。
 * `liveCatalog` 未给（这次没有网关现行清单）→ 不出标记，与下发「不可达不剔」同口径。
 */
function RetiredAgentModelMark({
  model,
  liveCatalog,
}: {
  model: string | undefined;
  liveCatalog: readonly CatalogModelIdRow[] | undefined;
}): JSX.Element | null {
  if (!model || !liveCatalog || !isRetiredFromLiveCatalog(liveCatalog, model)) return null;
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <span className="font-mono text-xs text-dosia-text-secondary line-through">{model}</span>
      <span
        data-testid="agent-model-retired-badge"
        className="rounded border border-dosia-warning/40 bg-dosia-warning/10 px-1.5 py-0.5 text-[11px] text-dosia-warning"
      >
        模型已下线 · 员工端按跟随主脑运行
      </span>
    </span>
  );
}

/** 待审 Agent 提交内容（AgentManifestV1 JSON）里指定的模型；解析不了 / 没指定 → undefined。 */
function pendingAgentModel(item: { kind: "skill" | "agent"; content: string }): string | undefined {
  if (item.kind !== "agent") return undefined;
  return parseAgentManifest(item.content)?.profile.model?.trim() || undefined;
}

// ---------- 分区 1 · 技能与 Agent ----------

/** 镜像 admin-portal orgs-reviews 透传的 dosia-enterprise PendingReviewItem。 */
interface SkillAgentReviewItem {
  kind: "skill" | "agent";
  submissionFileId: string;
  name: string;
  displayName?: string;
  submitter?: string;
  targetScope: "company" | "department";
  roleSlug?: string;
  baseVersion: number;
  content: string;
}

interface SkillAgentPendingResponse {
  items: SkillAgentReviewItem[];
  channelDisabled?: boolean;
}

/** promote 第一步（无 confirmToken）响应。 */
interface PromotePreviewResponse {
  requiresConfirm?: boolean;
  confirmToken?: string;
  preview?: {
    skillName?: string;
    targetScope?: string;
    roleSlug?: string;
    publishedRelativePath?: string;
    contentBase64?: string;
  };
}

function scopeLabel(item: Pick<SkillAgentReviewItem, "targetScope" | "roleSlug">): string {
  return item.targetScope === "company" ? "全公司" : `角色 ${item.roleSlug ?? "?"}`;
}

/** base64 → UTF-8 文本（预览用；解不开时如实提示而非裸抛）。 */
function decodeBase64Utf8(b64: string): string {
  try {
    return new TextDecoder().decode(
      Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)),
    );
  } catch {
    return "（内容解码失败）";
  }
}

interface ConfirmingState {
  item: SkillAgentReviewItem;
  confirmToken: string;
  preview: NonNullable<PromotePreviewResponse["preview"]>;
}

function SkillAgentReviewSection(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  // org-admin 审自己 org，super/support 代任意组织审批（canManageOrg 集中派生）。
  const canManage = canManageOrg(auth?.scope);
  const pendingQ = useApiQuery<SkillAgentPendingResponse>(
    canManage ? reviewsPendingPath(org.code) : null,
    [org.code, canManage],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [confirming, setConfirming] = useState<ConfirmingState | null>(null);
  const liveCatalog = useRetiredMarksCatalog(org.code);

  const startPromote = async (item: SkillAgentReviewItem): Promise<void> => {
    if (!auth) return;
    setBusyId(item.submissionFileId);
    setActionError(null);
    try {
      // 第一步：不带 confirmToken → 上游只返预览，不发布。
      const res = await apiCall<PromotePreviewResponse>(
        "POST",
        reviewsPromotePath(org.code),
        {
          token: auth.token,
          body: {
            kind: item.kind,
            submissionFileId: item.submissionFileId,
            name: item.name,
            targetScope: item.targetScope,
            ...(item.targetScope === "department" ? { roleSlug: item.roleSlug } : {}),
            baseVersion: item.baseVersion,
          },
        },
      );
      if (res.requiresConfirm && res.confirmToken) {
        setConfirming({ item, confirmToken: res.confirmToken, preview: res.preview ?? {} });
      } else {
        // 上游未要求确认（协议演进兜底）→ 视为已发布。
        await pendingQ.refetch();
      }
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "发起审批失败");
    } finally {
      setBusyId(null);
    }
  };

  const confirmPromote = async (): Promise<void> => {
    if (!auth || !confirming) return;
    setBusyId(confirming.item.submissionFileId);
    setActionError(null);
    try {
      // 第二步：带 confirmToken → 真发布。targetScope/roleSlug 必须与第一步完全一致
      // （confirmToken 绑定 targetFingerprint，见 enterprise/promote-confirm-store.ts）。
      await apiCall("POST", reviewsPromotePath(org.code), {
        token: auth.token,
        body: {
          kind: confirming.item.kind,
          submissionFileId: confirming.item.submissionFileId,
          name: confirming.item.name,
          targetScope: confirming.item.targetScope,
          ...(confirming.item.targetScope === "department"
            ? { roleSlug: confirming.item.roleSlug }
            : {}),
          baseVersion: confirming.item.baseVersion,
          confirmToken: confirming.confirmToken,
        },
      });
      setConfirming(null);
      await pendingQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "发布失败");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (item: SkillAgentReviewItem): Promise<void> => {
    if (!auth) return;
    setBusyId(item.submissionFileId);
    setActionError(null);
    try {
      await apiCall("POST", reviewsRejectPath(org.code), {
        token: auth.token,
        body: {
          kind: item.kind,
          submissionFileId: item.submissionFileId,
          baseVersion: item.baseVersion,
          targetScope: item.targetScope,
          ...(item.targetScope === "department" ? { roleSlug: item.roleSlug } : {}),
          rejectReason: rejectReason.trim() || undefined,
        },
      });
      setRejectingId(null);
      setRejectReason("");
      await pendingQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "驳回失败");
    } finally {
      setBusyId(null);
    }
  };

  const items = pendingQ.data?.items ?? [];

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">技能与 Agent</h3>
        <p className="text-sm text-dosia-text-secondary">
          员工提交的技能 / Agent。通过前先预览发布内容与落点，确认后发布到公司池或角色目录。
        </p>
      </div>
      {!canManage ? (
        <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          请先登录管理员账号后审批技能 / Agent 提交。
        </div>
      ) : pendingQ.data?.channelDisabled ? (
        <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          技能/Agent 审批通道未配置，请联系 DOSIA 运营。
        </div>
      ) : (
        <>
          {actionError && <ErrorBanner onDismiss={() => setActionError(null)}>{actionError}</ErrorBanner>}
          {pendingQ.error && <ErrorBanner>{pendingQ.error.message}</ErrorBanner>}
          {pendingQ.loading && items.length === 0 ? (
            <p className="text-sm text-dosia-text-dim">加载中…</p>
          ) : items.length === 0 ? (
            <Empty title="暂无待审技能 / Agent 提交" />
          ) : (
            <div className="space-y-3">
              {items.map((it) => (
                <div key={it.submissionFileId} className="rounded-lg border border-dosia-border p-4 space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{it.displayName || it.name}</span>
                        <span className="shrink-0 rounded border border-dosia-border px-1.5 py-0.5 text-[11px] text-dosia-text-secondary">
                          {it.kind === "skill" ? "技能" : "Agent"}
                        </span>
                        <RetiredAgentModelMark model={pendingAgentModel(it)} liveCatalog={liveCatalog} />
                      </div>
                      <div className="text-xs text-dosia-text-secondary mt-0.5">
                        提交人 {it.submitter ?? "未知"} · 发布到 {scopeLabel(it)} · 基线 v{it.baseVersion}
                      </div>
                      <details className="mt-1">
                        <summary className="text-xs text-dosia-text-secondary cursor-pointer select-none">
                          查看提交内容
                        </summary>
                        <pre className="mt-1 max-h-64 overflow-auto rounded bg-dosia-bg-lighter p-2 text-xs whitespace-pre-wrap break-all">
                          {it.content}
                        </pre>
                      </details>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <PrimaryButton
                        disabled={busyId === it.submissionFileId}
                        onClick={() => void startPromote(it)}
                      >
                        通过
                      </PrimaryButton>
                      <SecondaryButton
                        disabled={busyId === it.submissionFileId}
                        onClick={() => {
                          setRejectingId(rejectingId === it.submissionFileId ? null : it.submissionFileId);
                          setRejectReason("");
                        }}
                      >
                        驳回
                      </SecondaryButton>
                    </div>
                  </div>
                  {rejectingId === it.submissionFileId && (
                    <div className="flex items-center gap-2 pt-1">
                      <TextInput
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="驳回原因（可选）"
                      />
                      <SecondaryButton disabled={busyId === it.submissionFileId} onClick={() => void reject(it)}>
                        确认驳回
                      </SecondaryButton>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        open={confirming != null}
        onClose={() => setConfirming(null)}
        title="确认发布"
        size="lg"
        footer={
          <>
            <SecondaryButton onClick={() => setConfirming(null)}>取消</SecondaryButton>
            <PrimaryButton
              disabled={busyId === confirming?.item.submissionFileId}
              onClick={() => void confirmPromote()}
            >
              确认发布
            </PrimaryButton>
          </>
        }
      >
        {confirming && (
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-dosia-text-secondary">名称：</span>
              {confirming.preview.skillName ?? confirming.item.name}
            </div>
            <div>
              <span className="text-dosia-text-secondary">发布到：</span>
              {scopeLabel({
                targetScope:
                  confirming.preview.targetScope === "company" ? "company" : confirming.item.targetScope,
                roleSlug: confirming.preview.roleSlug ?? confirming.item.roleSlug,
              })}
            </div>
            {confirming.preview.publishedRelativePath && (
              <div className="font-mono text-xs break-all">
                <span className="font-sans text-dosia-text-secondary">落点：</span>
                {confirming.preview.publishedRelativePath}
              </div>
            )}
            {confirming.preview.contentBase64 && (
              <details>
                <summary className="text-xs text-dosia-text-secondary cursor-pointer select-none">
                  查看发布内容
                </summary>
                <pre className="mt-1 max-h-64 overflow-auto rounded bg-dosia-bg-lighter p-2 text-xs whitespace-pre-wrap break-all">
                  {decodeBase64Utf8(confirming.preview.contentBase64)}
                </pre>
              </details>
            )}
          </div>
        )}
      </Modal>
    </section>
  );
}

// ---------- 分区 2 · MCP（原 McpReviewPanel 逻辑原样搬入） ----------

/** Mirror of the admin-portal mcp-review serialize() shape. */
interface McpReviewItem {
  submissionId: string;
  submitter: string;
  submitterRole: string;
  name: string;
  entry: {
    type: "stdio" | "http" | "sse" | "sdk";
    command?: string;
    args?: string[];
    url?: string;
    description?: string;
  };
  target: { kind: "org" } | { kind: "roles"; roleCodes: string[] };
  submittedAt: string;
}

function targetLabel(t: McpReviewItem["target"]): string {
  return t.kind === "org" ? "全组织" : `团队：${t.roleCodes.join("、")}`;
}

function transportLabel(e: McpReviewItem["entry"]): string {
  if (e.type === "stdio") return `stdio · ${e.command ?? ""}${e.args?.length ? " " + e.args.join(" ") : ""}`;
  return `${e.type} · ${e.url ?? ""}`;
}

function McpReviewSection(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const pendingQ = useApiQuery<McpReviewItem[]>(mcpReviewPendingPath(org.code), [org.code]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const approve = async (id: string): Promise<void> => {
    if (!auth) return;
    setBusyId(id);
    setActionError(null);
    try {
      await apiCall("POST", mcpReviewApprovePath(org.code, id), { token: auth.token });
      await pendingQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "通过失败");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id: string): Promise<void> => {
    if (!auth) return;
    setBusyId(id);
    setActionError(null);
    try {
      await apiCall("POST", mcpReviewRejectPath(org.code, id), {
        token: auth.token,
        body: { reason: rejectReason.trim() },
      });
      setRejectingId(null);
      setRejectReason("");
      await pendingQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "驳回失败");
    } finally {
      setBusyId(null);
    }
  };

  const items = pendingQ.data ?? [];

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">MCP</h3>
        <p className="text-sm text-dosia-text-secondary">
          员工提交的自建 MCP server。通过后写入目标角色配置（role.dosia.mcpServers，唯一权威），该角色员工下次登录自动获得。
        </p>
      </div>
      {actionError && <ErrorBanner onDismiss={() => setActionError(null)}>{actionError}</ErrorBanner>}
      {pendingQ.error && <ErrorBanner>{pendingQ.error.message}</ErrorBanner>}
      {pendingQ.loading && items.length === 0 ? (
        <p className="text-sm text-dosia-text-dim">加载中…</p>
      ) : items.length === 0 ? (
        <Empty title="暂无待审 MCP 提交" />
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.submissionId} className="rounded-lg border border-dosia-border p-4 space-y-2">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs text-dosia-text-secondary mt-0.5">
                    提交人 {it.submitter}（{it.submitterRole}） · 发布到 {targetLabel(it.target)}
                  </div>
                  <div className="text-xs text-dosia-text-secondary mt-1 font-mono break-all">{transportLabel(it.entry)}</div>
                  {it.entry.description && <div className="text-xs text-dosia-text-dim mt-1">{it.entry.description}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <PrimaryButton disabled={busyId === it.submissionId} onClick={() => void approve(it.submissionId)}>
                    通过
                  </PrimaryButton>
                  <SecondaryButton
                    disabled={busyId === it.submissionId}
                    onClick={() => { setRejectingId(rejectingId === it.submissionId ? null : it.submissionId); setRejectReason(""); }}
                  >
                    驳回
                  </SecondaryButton>
                </div>
              </div>
              {rejectingId === it.submissionId && (
                <div className="flex items-center gap-2 pt-1">
                  <TextInput
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="驳回原因（可选）"
                  />
                  <SecondaryButton disabled={busyId === it.submissionId} onClick={() => void reject(it.submissionId)}>
                    确认驳回
                  </SecondaryButton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- 分区 3 · 已发布 Agent 字段级编辑（PR-C） ----------

/** SessionProfile 的五个能力轴（顺序即视觉稿的行序）。真白名单语义：留空 = 一个都没有。 */
type CapAxis = "skills" | "dosiaTools" | "mcpServers" | "nativeTools" | "subagents";

interface AxisMeta {
  axis: CapAxis;
  label: string;
  hint: string;
  /** picker：true = 从 catalog 候选选（nativeTools/dosiaTools）；false = 自由输入（无权威闭集）。 */
  /** 有服务端权威闭集 → 渲染下拉候选（选中即合法，无需自由输入）。nativeTools/dosiaTools 用它。 */
  showPicker: boolean;
  /** 无权威闭集（或闭集不完整，如 mcpServers 只是官方推荐子集）→ 保留自由输入框。
   *  可与 showPicker 同时为 true（mcpServers：候选是常用项点选捷径，非全集，不服务端校验）。 */
  showFreeInput: boolean;
  addPlaceholder: string;
}

const AXES: AxisMeta[] = [
  // skills：候选 = 组织已发布技能 ∪ 插件自带技能名（服务端目录）；目录不保证全集（插件滞后），
  // 故 picker + 自由输入并存，服务端不据此校验。
  { axis: "skills", label: "技能", hint: "这个 Agent 能调用的技能 —— 下拉是已发布 / 插件技能，未列出的可手动输入；真白名单，留空 = 一个都没有", showPicker: true, showFreeInput: true, addPlaceholder: "技能名（如 content-teardown）" },
  { axis: "dosiaTools", label: "应用工具", hint: "DOSIA 自带的应用工具（联网搜索 / 生成图片 / 发布网页 …）—— 真白名单", showPicker: true, showFreeInput: false, addPlaceholder: "" },
  // mcpServers：候选 = 官方推荐目录 ∪ 企业插件声明的 MCP server 名（聚光等），只是常用项点选
  // 捷径 —— 目录不保证全集，故保留自由输入；服务端也不校验这一轴（目录不完整会误杀合法名字）。
  { axis: "mcpServers", label: "MCP 连接器", hint: "外部 MCP 服务（浏览器操控 / 插件连接器等）—— 下拉是常用推荐，未列出的可手动输入", showPicker: true, showFreeInput: true, addPlaceholder: "连接器名（如插件自带的 MCP）" },
  { axis: "nativeTools", label: "内置工具", hint: "可门控的内置工具（跑命令 / 写文件 / 联网抓取 …）—— 只读检索类恒开，不用列", showPicker: true, showFreeInput: false, addPlaceholder: "" },
  // subagents：候选 = withBuiltinSpecialists() 名册（通用专家 + 各簇窄 agent）；同样 picker +
  // 自由输入并存，服务端不校验。
  { axis: "subagents", label: "可派子 Agent", hint: "允许它派生的子 Agent —— 下拉是内置专家 / 窄簇助手，未列出的可手动输入；不需要并行干活就留空", showPicker: true, showFreeInput: true, addPlaceholder: "子 Agent 名" },
];

type DraftCapabilities = Record<CapAxis, string[]>;

interface EditorDraft {
  summary: PublishedOrgAgentSummary;
  capabilities: DraftCapabilities;
  targetScope: "company" | "department";
  roles: string[];
}

function toDraftCapabilities(s: PublishedOrgAgentSummary): DraftCapabilities {
  const c = s.capabilities;
  return {
    skills: [...(c.skills ?? [])],
    dosiaTools: [...(c.dosiaTools ?? [])],
    mcpServers: [...(c.mcpServers ?? [])],
    nativeTools: [...(c.nativeTools ?? [])],
    subagents: [...(c.subagents ?? [])],
  };
}

/** 两个字符串集合的增删差（保序）。 */
function listDiff(before: string[], after: string[]): { added: string[]; removed: string[] } {
  return {
    added: after.filter((x) => !before.includes(x)),
    removed: before.filter((x) => !after.includes(x)),
  };
}

/** 一行能力轴：chip 列表 + ✕ 删除 + 添加（picker 或自由输入）。 */
function AxisEditorRow({
  meta,
  values,
  candidates,
  onChange,
}: {
  meta: AxisMeta;
  values: string[];
  candidates: string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  const [draftName, setDraftName] = useState("");
  const add = (raw: string): void => {
    const name = raw.trim();
    if (!name || values.includes(name)) return;
    onChange([...values, name]);
    setDraftName("");
  };
  const remove = (name: string): void => onChange(values.filter((v) => v !== name));
  const available = candidates.filter((c) => !values.includes(c));

  return (
    <div className="space-y-1.5">
      <div className="text-xs text-dosia-text-secondary">{meta.hint}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium mr-1">{meta.label}</span>
        {values.length === 0 && <span className="text-xs text-dosia-text-dim">留空 · 一个都没有</span>}
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded border border-dosia-border bg-dosia-bg-lighter px-1.5 py-0.5 text-xs"
          >
            {v}
            <button
              type="button"
              className="text-dosia-text-dim hover:text-dosia-text"
              aria-label={`删除 ${v}`}
              onClick={() => remove(v)}
            >
              ✕
            </button>
          </span>
        ))}
        {meta.showPicker && (
          <select
            className="rounded border border-dosia-border bg-dosia-bg px-1.5 py-0.5 text-xs text-dosia-text"
            value=""
            aria-label={`添加${meta.label}`}
            onChange={(e) => {
              if (e.target.value) add(e.target.value);
            }}
          >
            <option value="">+ 添加{meta.label}</option>
            {available.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
        {meta.showFreeInput && (
          <span className="inline-flex items-center gap-1">
            <TextInput
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={meta.addPlaceholder}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add(draftName);
                }
              }}
            />
            <SecondaryButton onClick={() => add(draftName)}>+ 添加{meta.label}</SecondaryButton>
          </span>
        )}
      </div>
    </div>
  );
}

/** 一个已发布 Agent 的就地编辑卡片。 */
function PublishedAgentEditor({
  summary,
  catalog,
  onCancel,
  onRepublished,
}: {
  summary: PublishedOrgAgentSummary;
  catalog: PublishedOrgAgentsResponse["catalog"];
  onCancel: () => void;
  onRepublished: () => Promise<void>;
}): JSX.Element {
  const auth = useAtomValue(authAtom);
  const { org } = useOutletContext<OrgDetailContext>();
  const [draft, setDraft] = useState<EditorDraft>(() => ({
    summary,
    capabilities: toDraftCapabilities(summary),
    targetScope: summary.targetScope,
    roles: [...summary.roles],
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  const setAxis = (axis: CapAxis, next: string[]): void =>
    setDraft((d) => ({ ...d, capabilities: { ...d.capabilities, [axis]: next } }));

  const candidatesFor = (axis: CapAxis): string[] =>
    axis === "nativeTools"
      ? catalog.nativeTools
      : axis === "dosiaTools"
        ? catalog.dosiaTools
        : axis === "mcpServers"
          ? catalog.mcpServers
          : axis === "skills"
            ? catalog.skills
            : axis === "subagents"
              ? catalog.subagents
              : [];

  const deptInvalid = draft.targetScope === "department" && draft.roles.length === 0;

  // Diff 摘要（改动：+ 新增 / - 移除）。
  const diffLines = useMemo(() => {
    const lines: string[] = [];
    for (const meta of AXES) {
      const { added, removed } = listDiff(summary.capabilities[meta.axis] ?? [], draft.capabilities[meta.axis]);
      for (const a of added) lines.push(`+ ${meta.label} ${a}`);
      for (const r of removed) lines.push(`− ${meta.label} ${r}`);
    }
    if (draft.targetScope !== summary.targetScope) {
      lines.push(`可见范围 → ${draft.targetScope === "company" ? "全公司" : "部门"}`);
    } else if (draft.targetScope === "department") {
      const { added, removed } = listDiff(summary.roles, draft.roles);
      for (const a of added) lines.push(`+ 部门 ${a}`);
      for (const r of removed) lines.push(`− 部门 ${r}`);
    }
    return lines;
  }, [draft, summary]);

  const republish = async (): Promise<void> => {
    if (!auth || deptInvalid) return;
    setBusy(true);
    setError(null);
    setReceipt(null);
    try {
      // 走唯一构建器（红线 #9）构造 wire manifest；服务端仍会 server-authoritative 重建。
      const manifest = buildAgentManifest({
        id: `agent_org_${draft.summary.slug}`,
        profile: {
          name: draft.summary.name,
          ...(draft.summary.description ? { description: draft.summary.description } : {}),
          ...(draft.summary.systemPrompt ? { systemPrompt: draft.summary.systemPrompt } : {}),
          capabilities: draft.capabilities,
          ...(draft.summary.model ? { model: draft.summary.model } : {}),
          ...(draft.summary.icon ? { icon: draft.summary.icon } : {}),
        },
        scope: draft.targetScope,
        ...(draft.targetScope === "department" ? { roles: draft.roles } : {}),
        submitter: "admin", // 占位；服务端以 actor 覆盖
      });
      await apiCall(
        "POST",
        reviewsPublishDirectPath(org.code),
        {
          token: auth.token,
          body: {
            manifest,
            targetScope: draft.targetScope,
            ...(draft.targetScope === "department" ? { publishRoleSlugs: draft.roles } : {}),
          },
        },
      );
      await onRepublished();
    } catch (err) {
      // 能力名校验回执（400）→ amber 提示；其它错误 → 红色 banner。
      if (err instanceof ApiError && err.status === 400 && err.code === "upstream-error") {
        setReceipt(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : "重新发布失败");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-dosia-accent/40 bg-dosia-bg-lighter p-4 space-y-3">
      <div>
        <div className="flex items-center gap-2">
          {draft.summary.icon && <span>{draft.summary.icon}</span>}
          <span className="font-medium">编辑「{draft.summary.name}」</span>
        </div>
        {draft.summary.description && (
          <div className="text-xs text-dosia-text-secondary mt-0.5">{draft.summary.description}</div>
        )}
      </div>

      {/* 可见范围 */}
      <div className="space-y-1.5">
        <div className="text-xs text-dosia-text-secondary">可见范围</div>
        <div className="flex items-center gap-4 text-sm">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={draft.targetScope === "company"}
              onChange={() => setDraft((d) => ({ ...d, targetScope: "company" }))}
            />
            全公司
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={draft.targetScope === "department"}
              onChange={() => setDraft((d) => ({ ...d, targetScope: "department" }))}
            />
            部门
          </label>
        </div>
        {draft.targetScope === "department" && (
          <div className="flex flex-wrap gap-2 pt-1">
            {catalog.departmentRoles.length === 0 && (
              <span className="text-xs text-dosia-text-dim">该组织暂无可选部门角色</span>
            )}
            {catalog.departmentRoles.map((r) => (
              <label key={r.templateId} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.roles.includes(r.templateId)}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      roles: e.target.checked
                        ? [...d.roles, r.templateId]
                        : d.roles.filter((x) => x !== r.templateId),
                    }))
                  }
                />
                {r.label}
              </label>
            ))}
          </div>
        )}
        {deptInvalid && <div className="text-xs text-amber-500">部门可见时至少选一个部门角色。</div>}
      </div>

      {/* 五轴能力面 */}
      <div className="space-y-3 border-t border-dosia-border pt-3">
        {AXES.map((meta) => (
          <AxisEditorRow
            key={meta.axis}
            meta={meta}
            values={draft.capabilities[meta.axis]}
            candidates={candidatesFor(meta.axis)}
            onChange={(next) => setAxis(meta.axis, next)}
          />
        ))}
      </div>

      {/* diff 摘要 */}
      <div className="border-t border-dosia-border pt-2 text-xs">
        <span className="text-dosia-text-secondary">改动：</span>
        {diffLines.length === 0 ? (
          <span className="text-dosia-text-dim">无改动</span>
        ) : (
          <span className="text-dosia-text">{diffLines.join("  ")}</span>
        )}
      </div>

      {receipt && (
        <div className="rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-600">
          {receipt}
        </div>
      )}
      {error && <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>}

      <div className="flex items-center justify-end gap-2">
        <SecondaryButton disabled={busy} onClick={onCancel}>
          取消
        </SecondaryButton>
        <PrimaryButton
          disabled={busy || deptInvalid || diffLines.length === 0}
          onClick={() => void republish()}
        >
          {draft.targetScope === "company" ? "重新发布给全公司" : "重新发布给所选部门"}
        </PrimaryButton>
      </div>

      <FeishuPublishSection orgCode={org.code} slug={draft.summary.slug} />
    </div>
  );
}

function PublishedAgentsSection(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const canManage = canManageOrg(auth?.scope);
  const publishedQ = useApiQuery<PublishedOrgAgentsResponse>(
    canManage ? reviewsPublishedPath(org.code) : null,
    [org.code, canManage],
  );
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const liveCatalog = useRetiredMarksCatalog(org.code);

  const items = publishedQ.data?.items ?? [];
  const catalog: PublishedOrgAgentsResponse["catalog"] =
    publishedQ.data?.catalog ?? { nativeTools: [], dosiaTools: [], mcpServers: [], pluginMcpServers: [], skills: [], subagents: [], departmentRoles: [] };

  const scopeBadge = (s: PublishedOrgAgentSummary): string =>
    s.targetScope === "company" ? "全公司" : `部门 · ${s.roles.length}`;

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">已发布 Agent</h3>
        <p className="text-sm text-dosia-text-secondary">
          直接编辑已发给全公司 / 部门的 Agent 的能力面与可见范围，一键重新发布，全员下次同步生效。
        </p>
      </div>
      {!canManage ? (
        <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          请先登录管理员账号后管理已发布 Agent。
        </div>
      ) : (
        <>
          {publishedQ.error && <ErrorBanner>{publishedQ.error.message}</ErrorBanner>}
          {publishedQ.loading && items.length === 0 ? (
            <p className="text-sm text-dosia-text-dim">加载中…</p>
          ) : items.length === 0 ? (
            <Empty title="暂无已发布的 Agent" />
          ) : (
            <div className="space-y-3">
              {items.map((it) =>
                editingSlug === it.slug ? (
                  <PublishedAgentEditor
                    key={it.slug}
                    summary={it}
                    catalog={catalog}
                    onCancel={() => setEditingSlug(null)}
                    onRepublished={async () => {
                      setEditingSlug(null);
                      await publishedQ.refetch();
                    }}
                  />
                ) : (
                  <div
                    key={it.slug}
                    className="flex items-center justify-between gap-4 rounded-lg border border-dosia-border p-4"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {it.icon && <span className="shrink-0">{it.icon}</span>}
                      <span className="font-medium truncate">{it.name}</span>
                      <span className="shrink-0 rounded border border-dosia-border px-1.5 py-0.5 text-[11px] text-dosia-text-secondary">
                        {scopeBadge(it)}
                      </span>
                      <RetiredAgentModelMark model={it.model} liveCatalog={liveCatalog} />
                    </div>
                    <SecondaryButton onClick={() => setEditingSlug(it.slug)}>编辑</SecondaryButton>
                  </div>
                ),
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ---------- 页面 ----------

export function ReviewCenterPanel(): JSX.Element {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">审批中心</h2>
        <p className="text-sm text-dosia-text-secondary">
          员工提交物的统一审核入口：技能 / Agent / MCP。
        </p>
      </div>
      <SkillAgentReviewSection />
      <PublishedAgentsSection />
      <McpReviewSection />
    </div>
  );
}
